import {
  Autocomplete,
  Chip,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import { CreateMealAction } from '../../../../../store/create-meal-slice';
import { queryKeys } from '../../../../../utils/queryKeys';
import {
  areMealTagsEqual,
  dedupeMealTags,
  formatMealTagLabel,
  getMealAssignedTagIds,
  mapApiTagToMealTag,
} from '../../../../../utils/mealTagUtils';
import { sortTagsBySequence } from '../../../../../utils/tagUtils';
import {
  getAllTags,
} from '../../../tag_management/TagManagementApi';
import { patchMealAssignedTags, reloadMeal } from '../CreateFoodBoxApi';
import AddNewTagDialog from './AddNewTagDialog';

const AddTagsComponent = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const location = useLocation();
  const tags = useSelector((state) => state.createMeal.tags);
  const { externalId } = useSelector((state) => state.createMeal);
  const { language } = useSelector((state) => state.globalUi);
  const sortedTags = sortTagsBySequence(tags);
  const isEditMode = location.pathname.includes('edit-food-box');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data, isLoading, isError } = useQuery(
    queryKeys.getAllTagsData,
    getAllTags,
    {
      refetchOnWindowFocus: false,
    }
  );

  const patchAssignedTagsMutation = useMutation(patchMealAssignedTags, {
    onSuccess: async (_, variables) => {
      await reloadMeal(queryClient, variables.externalId);
      await queryClient.invalidateQueries(queryKeys.getMealData);
    },
  });

  const availableTags = useMemo(
    () =>
      sortTagsBySequence(
        (data?.data ?? []).map((tag) => mapApiTagToMealTag(tag))
      ),
    [data?.data]
  );

  const selectableTags = useMemo(
    () => sortTagsBySequence(dedupeMealTags([...availableTags, ...sortedTags])),
    [availableTags, sortedTags]
  );

  const persistTags = async (nextTags, previousTags = sortedTags) => {
    const normalizedTags = sortTagsBySequence(dedupeMealTags(nextTags));
    dispatch(CreateMealAction.setTags(normalizedTags));

    if (!isEditMode) {
      return;
    }

    const assignedTagIds = getMealAssignedTagIds(normalizedTags, data?.data ?? []);

    if (assignedTagIds.length !== normalizedTags.length) {
      dispatch(CreateMealAction.setTags(previousTags));
      toast.error('One or more selected tags could not be matched to saved tags');
      return;
    }

    try {
      await patchAssignedTagsMutation.mutateAsync({
        externalId,
        assignedTagIds,
      });
    } catch (error) {
      dispatch(CreateMealAction.setTags(previousTags));
      toast.error(error?.response?.data?.message ?? 'Unable to update meal tags');
    }
  };

  const handleDelete = (tag) => {
    void persistTags(
      sortedTags.filter((currentTag) => !areMealTagsEqual(currentTag, tag)),
      sortedTags
    );
  };

  const handleTagSelectionChange = (_, nextTags) => {
    if (nextTags.length > sortedTags.length) {
      setIsDropdownOpen(false);
    }

    void persistTags(nextTags, sortedTags);
  };

  const handleTagCreated = (tag) => {
    void persistTags([...sortedTags, tag], sortedTags);
  };

  return (
    <Grid2 container columnSpacing={1} rowSpacing={1}>
      <Grid2 xs={2}>
        <Typography variant='s16_w600' component='span'>
          Tag Management:*
        </Typography>
      </Grid2>
      <Grid2 xs={7} direction='column'>
        <Autocomplete
          multiple
          options={selectableTags}
          value={sortedTags}
          open={isDropdownOpen}
          loading={isLoading || patchAssignedTagsMutation.isLoading}
          filterSelectedOptions
          onOpen={() => setIsDropdownOpen(true)}
          onClose={() => setIsDropdownOpen(false)}
          onChange={handleTagSelectionChange}
          isOptionEqualToValue={(option, value) => areMealTagsEqual(option, value)}
          getOptionLabel={(option) => formatMealTagLabel(option, language)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder='Search and select existing tags'
              helperText={
                isError
                  ? 'Unable to load existing tags right now. You can still create one below.'
                  : 'Assign existing tags to this meal, or create a new one.'
              }
              error={isError}
            />
          )}
          renderTags={() => null}
        />
        <Stack direction='row' spacing={1} mt={1} display='flex' flexWrap='wrap'>
          {sortedTags.map((tag) => {
            return (
              <Chip
                key={`${tag.id ?? 'tag'}-${tag.english}-${tag.nor}-${tag.sequence}`}
                label={formatMealTagLabel(tag, language)}
                onDelete={() => handleDelete(tag)}
                sx={{
                  backgroundColor: '#FFE8E1',
                  color: '#342C47',
                  fontFamily: 'Inter',
                  fontSize: '1.4rem',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: 'red',
                  },
                }}
              />
            );
          })}
        </Stack>
      </Grid2>
      <Grid2 xs={3}>
        <AddNewTagDialog
          availableTags={data?.data ?? []}
          onTagCreated={handleTagCreated}
        />
      </Grid2>
    </Grid2>
  );
};

export default AddTagsComponent;
